// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, AlertTriangle, Phone, Home } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { VCIN_COLORS } from '../styles/constants';

// Threat level color mapping
const THREAT_COLORS = {
  0: { bg: '#E8F5E9', border: '#4CAF50', text: '#2E7D32', label: 'NO THREAT' },
  1: { bg: '#FFF8E1', border: '#FFC107', text: '#F57F17', label: 'LOW CONCERN' },
  2: { bg: '#FFF3E0', border: '#FF9800', text: '#E65100', label: 'MODERATE' },
  3: { bg: '#FFEBEE', border: '#F44336', text: '#C62828', label: 'ELEVATED' },
  4: { bg: '#FFCDD2', border: '#D32F2F', text: '#B71C1C', label: 'HIGH RISK' },
  5: { bg: '#B71C1C', border: '#B71C1C', text: '#FFFFFF', label: 'CRITICAL' }
};

export default function SightingConfirmationScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from navigation state
  const { vcinReference, threatLevel, threatLabel, actionRequired, vrn } = location.state || {};
  
  // Fallback if no state (shouldn't happen in normal flow)
  if (!vcinReference) {
    return (
      <div className="min-h-screen bg-vcin-bg flex flex-col items-center justify-center px-6">
        <p className="text-vcin-secondary mb-4">No confirmation data found</p>
        <button
          onClick={() => navigate('/')}
          className="h-12 px-6 text-white rounded-xl font-semibold"
          style={{ backgroundColor: VCIN_COLORS.primary }}
        >
          Go Home
        </button>
      </div>
    );
  }
  
  const threatConfig = THREAT_COLORS[threatLevel] || THREAT_COLORS[0];
  const isHighThreat = threatLevel >= 3;
  const isCritical = threatLevel >= 4;

  return (
    <div className="min-h-screen bg-vcin-bg flex flex-col" data-testid="sighting-confirmation-screen">
      {/* Header */}
      <div className="bg-white shadow-sm px-5 py-4">
        <h1 className="font-semibold text-vcin-text text-lg text-center">Sighting Recorded</h1>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 py-6 pb-32 overflow-auto">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#E8F5E9' }}
          >
            <Check className="w-10 h-10 text-green-600" />
          </div>
        </div>

        {/* VCIN Reference Card */}
        <div 
          className="bg-white rounded-2xl p-6 shadow-sm mb-6"
          data-testid="vcin-reference-card"
        >
          <p className="text-vcin-secondary text-sm text-center mb-2">VCIN Reference Number</p>
          <p 
            className="text-2xl font-bold text-center tracking-wider text-vcin-primary"
            data-testid="vcin-reference"
          >
            {vcinReference}
          </p>
          <p className="text-vcin-secondary text-xs text-center mt-2">
            Save this reference for your records
          </p>
        </div>

        {/* Vehicle Info */}
        <div className="bg-white rounded-2xl p-5 shadow-sm mb-6">
          <p className="text-vcin-secondary text-sm mb-2">Vehicle Registration</p>
          <p 
            className="text-xl font-bold tracking-widest text-center py-2 px-4 rounded"
            style={{ 
              backgroundColor: '#FFC425',
              fontFamily: '"Courier New", Courier, monospace',
              letterSpacing: '0.15em'
            }}
            data-testid="vrn-display"
          >
            {vrn}
          </p>
        </div>

        {/* Threat Level Card */}
        <div 
          className="rounded-2xl p-5 mb-6"
          style={{ 
            backgroundColor: threatConfig.bg,
            border: `2px solid ${threatConfig.border}`
          }}
          data-testid="threat-level-card"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium" style={{ color: threatConfig.text }}>
              Current Threat Level
            </span>
            {isHighThreat && (
              <AlertTriangle className="w-5 h-5" style={{ color: threatConfig.text }} />
            )}
          </div>
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl"
              style={{ 
                backgroundColor: threatConfig.border,
                color: threatLevel >= 4 ? '#FFFFFF' : threatConfig.text
              }}
              data-testid="threat-level-badge"
            >
              {threatLevel}
            </div>
            <div>
              <p 
                className="font-bold text-lg"
                style={{ color: threatConfig.text }}
                data-testid="threat-label"
              >
                {threatLabel || threatConfig.label}
              </p>
            </div>
          </div>
        </div>

        {/* Action Required */}
        {actionRequired && (
          <div 
            className={`rounded-2xl p-5 mb-6 ${isCritical ? 'bg-red-600' : 'bg-white'}`}
            style={!isCritical ? { border: '1px solid #E8EAF0' } : {}}
            data-testid="action-required-card"
          >
            <p 
              className={`text-sm font-medium mb-2 ${isCritical ? 'text-white' : 'text-vcin-secondary'}`}
            >
              Action Required
            </p>
            <p 
              className={`font-semibold ${isCritical ? 'text-white text-lg' : 'text-vcin-text'}`}
              data-testid="action-required-text"
            >
              {actionRequired}
            </p>
          </div>
        )}

        {/* Call 999/101 Button for High Threat */}
        {isHighThreat && (
          <a
            href={isCritical ? "tel:999" : "tel:101"}
            className="w-full h-14 flex items-center justify-center gap-3 rounded-xl font-bold text-lg mb-4 active:scale-[0.98] transition-transform"
            style={{ 
              backgroundColor: isCritical ? '#B71C1C' : '#FF9800',
              color: '#FFFFFF'
            }}
            data-testid="call-emergency-btn"
          >
            <Phone className="w-5 h-5" />
            <span>{isCritical ? 'Call 999' : 'Call 101 Non-Emergency'}</span>
          </a>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/log-sighting')}
            className="w-full h-14 text-white font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            style={{ backgroundColor: VCIN_COLORS.primary }}
            data-testid="log-another-btn"
          >
            Log Another Sighting
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full h-14 bg-white border text-vcin-text font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            style={{ borderColor: '#D8DCE4' }}
            data-testid="go-home-btn"
          >
            <Home className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav activeTab="log" />
    </div>
  );
}
